import Alert from "components/common/Alert";
import { useEffect, useMemo, useRef, useState } from "react";
import { getExhibitor } from "api/exhibitor.api";
import { useProductCategory } from "components/productCategory/hooks/useProductCategory";
import { useCurrency } from "components/currency/hooks/useCurrency";
import { getImageUrl } from "utils/imageHelper";


export default function ProductsForm({
  initialData = null,
  onSubmit,
  submitLabel,
  onBack,
}) {
  const { categories, loadCategoriesByOrganizer } = useProductCategory();

  const [exhibitors, setExhibitors] = useState([]);
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const alertRef = useRef(null);
  const { data: currencies, loadCurrencies } = useCurrency();

  const [formData, setFormData] = useState({
    company_id: "",
    exhibitor_id: "",
    product_category_id: "",
    product_name: "",
    price: "",
    currency_id: "",
    website: "",
    short_short_description: "",
    status: "active",
    is_sample: false,
    sample_start_date: "",
    sample_end_date: "",
    productImages: [],
    default_image: "",
  });

  const formatInputDateTimeLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");

  const yyyy = d.getFullYear();
  const MM = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mm = pad(d.getMinutes());

  return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
};

  useEffect(() => {
    if (!initialData || !initialData._id) return;

    setFormData({
      company_id: initialData.company_id?._id || "",
      exhibitor_id: initialData.exhibitor_id?._id || "",
      product_category_id: initialData.product_category_id?._id || "",
      product_name: initialData.product_name || "",
      price: initialData.price || "",
      currency_id: initialData.currency_id?._id || "",
      website: initialData.website || "",
      short_description: initialData.short_description || "",
      status: initialData.status || "active",
      is_sample: Boolean(initialData.is_sample),
      sample_start_date: initialData.sample_start_date
      ? formatInputDateTimeLocal(initialData.sample_start_date)
      : "",
      sample_end_date: initialData.sample_end_date
      ? formatInputDateTimeLocal(initialData.sample_end_date)
      : "",

      productImages: initialData.productImages || [],
      default_image: initialData.default_image
      ? JSON.stringify({ type: "existing", path: initialData.default_image })
      : "",
    });
    
  }, [initialData]);

  useEffect(() => {
    loadCurrencies();
  }, []);

  

 useEffect(() => {
  const companyId = localStorage.getItem("company_id");
  if (!companyId) return;

  getExhibitor(companyId)
    .then((res) => setExhibitors(res.data || []))
    .catch(() => setExhibitors([]));

    
  loadCategoriesByOrganizer(companyId);
}, []);

  useEffect(() => {
    if (alert && alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [alert]);

  const defaultMeta = useMemo(() => {
    try {
      return formData.default_image
        ? JSON.parse(formData.default_image)
        : null;
    } catch {
      return null;
    }
  }, [formData.default_image]);

  const handleImages = (e) => {
    const files = Array.from(e.target.files);
    setFormData((p) => ({
      ...p,
      productImages: [...p.productImages, ...files],
    }));
  };

  const removeImage = (index) => {
    setFormData((p) => {
      const imgs = [...p.productImages];
      const removed = imgs.splice(index, 1)[0];

      return {
        ...p,
        productImages: imgs,
        removed_images: [
          ...(p.removed_images || []),
          ...(typeof removed === "string" ? [removed] : []),
        ],
        default_image:
          defaultMeta?.path === removed ? "" : p.default_image,
      };
    });
  };


  const setDefaultImage = (img, index) => {
    setFormData((p) => ({
      ...p,
      default_image:
        img instanceof File
          ? JSON.stringify({ type: "new", index })
          : JSON.stringify({ type: "existing", path: img }),
    }));
  };

  const validate = () => {
    const e = {};

    if (!formData.exhibitor_id) e.exhibitor_id = "Exhibitor is required";
    if (!formData.product_category_id)
      e.product_category_id = "Product category is required";
    if (!formData.product_name?.trim())
      e.product_name = "Product name is required";
    if (!formData.price) e.price = "Price is required";

    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      setAlert("Please fix the errors below");
      return;
    }

    setErrors({});
    setAlert("");

    const fd = new FormData();

    Object.entries(formData).forEach(([k, v]) => {
       if (
      k === "productImages" ||
      k === "removed_images" ||
      k === "default_image" ||
      k === "is_sample" || 
      k === "sample_start_date" || 
      k === "sample_end_date"  
    )
      return;
      if (v !== null && v !== "") fd.append(k, v);
    });

    formData.productImages.forEach((img) => {
      if (img instanceof File) {
        fd.append("productImages", img);
      }
    });

    fd.append(
      "existing_images",
      JSON.stringify(
        formData.productImages.filter((i) => typeof i === "string")
      )
    );

    if (formData.removed_images?.length) {
      fd.append(
        "removed_images",
        JSON.stringify(formData.removed_images)
      );
    }

    if (formData.default_image) {
      fd.append("default_image", formData.default_image);
    }

    fd.append("is_sample", formData.is_sample);

    if (formData.is_sample) {
    fd.append(
        "sample_start_date",
        new Date(formData.sample_start_date).toISOString()
    );
    fd.append(
        "sample_end_date",
        new Date(formData.sample_end_date).toISOString()
    );
    }


    setSubmitting(true);
    try {
      await onSubmit(fd);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-10">
      {alert && (
        <div ref={alertRef}>
          <Alert type="error" message={alert} onClose={() => setAlert("")} />
        </div>
      )}

      <Section title="Product Details">
        <SelectInput
          label="Exhibitor"
          required
          value={formData.exhibitor_id}
          error={errors.exhibitor_id}
          onChange={(e) =>
            setFormData((p) => ({ ...p, exhibitor_id: e.target.value }))
          }
        >
          <option value="">Select Exhibitor</option>
          {exhibitors.map((e) => (
            <option key={e._id} value={e._id}>
              {e.title}
            </option>
          ))}
        </SelectInput>

        <SelectInput
          label="Product Category"
          required
          value={formData.product_category_id}
          error={errors.product_category_id}
          onChange={(e) =>
            setFormData((p) => ({
              ...p,
              product_category_id: e.target.value,
            }))
          }
        >
          <option value="">Select Product Category</option>
          {categories.map((c) => (
            <option key={c._id} value={c._id}>
              {c.title}
            </option>
          ))}
        </SelectInput>

       <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-4 gap-5">

          <div className="md:col-span-2">
            <Input
              label="Product Name"
              required
              value={formData.product_name}
              error={errors.product_name}
              onChange={(e) =>
                setFormData((p) => ({ ...p, product_name: e.target.value }))
              }
            />
          </div>
        
          <SelectInput
            label="Currency"
            required
            value={formData.currency_id}
            onChange={(e) =>
              setFormData((p) => ({
                ...p,
                currency_id: e.target.value,
              }))
            }
          >
            <option value="">Select Currency</option>

            {currencies.map((c) => (
              <option key={c._id} value={c._id}>
                {c.symbol} {c.title} ({c.code})
                {c.is_deleted ? " (Inactive)" : ""}
              </option>
            ))}
          </SelectInput>



          <Input
            label="Price"
            type="number"
            required
            step="0.01"
            min="0"
            value={formData.price}
            error={errors.price}
            onChange={(e) =>
              setFormData((p) => ({ ...p, price: e.target.value }))
            }
          />
        </div>

        <Input
          label="Website"
          value={formData.website}
          onChange={(e) =>
            setFormData((p) => ({ ...p, website: e.target.value }))
          }
        />

        <SelectInput
            label="Is Sample Available"
            value={formData.is_sample ? "yes" : "no"}
            onChange={(e) =>
                setFormData((p) => ({
                ...p,
                is_sample: e.target.value === "yes",
                sample_start_date: "",
                sample_end_date: "",
                }))
            }
            >
            <option value="no">No</option>
            <option value="yes">Yes</option>
        </SelectInput>

        {formData.is_sample && (
        <>
            <Input
            type="datetime-local"
            label="Start Date & Time"
            required
            value={formData.sample_start_date}
            error={errors.sample_start_date}
            onChange={(e) =>
                setFormData((p) => ({
                ...p,
                sample_start_date: e.target.value,
                }))
            }
            />

            <Input
            type="datetime-local"
            label="End Date & Time"
            required
            value={formData.sample_end_date}
            error={errors.sample_end_date}
            onChange={(e) =>
                setFormData((p) => ({
                ...p,
                sample_end_date: e.target.value,
                }))
            }
            />
        </>
        )}


        <Textarea
          label="Short Description"
          value={formData.short_description}
          onChange={(e) =>
            setFormData((p) => ({ ...p, short_description: e.target.value }))
          }
        />
      </Section>

      <Section title="Product Images">
        <div className="md:col-span-2 space-y-3">
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleImages}
            className="w-full rounded-xl border p-3"
          />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {formData.productImages.map((img, i) => {
              const src =
                typeof img === "string"
                  ? getImageUrl(img)
                  : URL.createObjectURL(img);

              const isDefault =
                (defaultMeta?.type === "new" && defaultMeta.index === i) ||
                (defaultMeta?.type === "existing" &&
                  defaultMeta.path === img);

              return (
                <div
                  key={i}
                  className={`relative border rounded-xl p-2 ${
                    isDefault ? "ring-2 ring-brand-500" : ""
                  }`}
                >
                  <img
                    src={src}
                    className="h-32 w-full object-cover rounded-lg"
                  />

                  <div className="mt-2 flex justify-between">
                    <button
                      type="button"
                      onClick={() => setDefaultImage(img, i)}
                      className="text-xs text-blue-600"
                    >
                      Set Default
                    </button>

                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      className="text-xs text-red-600"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              );
            })}

          </div>
        </div>
      </Section>

      <Section title="Status">
        <SelectInput
          label="Available Status"
          value={formData.status}
          onChange={(e) =>
            setFormData((p) => ({ ...p, status: e.target.value }))
          }
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </SelectInput>
      </Section>

      <div className="flex justify-between">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="rounded-xl bg-red-500 px-6 py-3 text-white"
          >
            Back
          </button>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="rounded-xl bg-brand-500 px-6 py-3 text-white"
        >
          {submitLabel || (initialData ? "Update Product" : "Create Product")}
        </button>
      </div>
    </form>
  );
}
function Section({ title, children }) {
  return (
    <section>
      <h3 className="mb-4 text-lg font-semibold">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {children}
      </div>
    </section>
  );
}
function Input({ label, required, error, ...props }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
       <input
        {...props}
        className={`w-full rounded-xl border p-3 ${
          error ? "border-red-500" : ""
        }`}
      />

      {error && (
        <p className="mt-1 text-xs text-red-500">
          {error}
        </p>
      )}
    </div>
  );
}

function Textarea({ label, required, error, ...props }) {
  return (
    <div className="md:col-span-2">
      <label className="mb-1 block text-sm font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <textarea
        {...props}
        className={`w-full rounded-xl border p-3 ${
          error ? "border-red-500" : ""
        }`}
      />

      {error && (
        <p className="mt-1 text-xs text-red-500">
          {error}
        </p>
      )}
    </div>
  );
}
function SelectInput({ label, required, error, children, ...props }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <select
        {...props}
        className={`w-full rounded-xl border p-3 ${
          error ? "border-red-500" : ""
        }`}
      >
        {children}
      </select>

      {error && (
        <p className="mt-1 text-xs text-red-500">
          {error}
        </p>
      )}
    </div>
  );
}
